import React from 'react';
import { TrendingUp, Activity, Globe } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const DashboardStats = ({
  stats,
  selectedGeo,
  onGeoChange,
  onMetricClick,
  activeMetric = 'spikes',
  selectedPeriod = 14,
  spikeListTotal = null,
}) => {
  const topGeo = stats?.top_growing_geo || 'N/A';
  const spikesPeriodDays = Number(stats?.spikes_period_days || selectedPeriod || 14);
  const activePeriodDays = Number(stats?.active_entities_period_days || selectedPeriod || 14);
  const hasSpikeListTotal = spikeListTotal !== null && spikeListTotal !== undefined;
  const periodLabel = `${activePeriodDays}d`;
  const handleMetricClick = (key) => {
    if (onMetricClick) {
      onMetricClick(key);
      return;
    }
    // Backward compatibility: old behavior only toggled GEO.
    if (key === 'topgeo' && topGeo && topGeo !== 'N/A') onGeoChange?.(topGeo);
    else onGeoChange?.(null);
  };
  
  const metrics = [
    {
      key: 'newbrands',
      label: 'New Brands',
      meta: periodLabel,
      value: stats?.new_verified_entities_period ?? stats?.new_entities_period ?? 0,
      subValue: `All new brands: ${(stats?.new_entities_period ?? 0).toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
      isActive: activeMetric === 'newbrands',
      onClick: () => handleMetricClick('newbrands'),
    },
    {
      key: 'entities',
      label: 'Verified Active',
      meta: periodLabel,
      value: stats?.verified_casinos_period ?? 0,
      icon: Activity,
      color: 'text-chart-1',
      bgColor: 'bg-chart-1/10',
      isActive: activeMetric === 'entities',
      onClick: () => handleMetricClick('entities'),
    },
    {
      key: 'spikes',
      label: 'Brands with Spikes',
      meta: `${spikesPeriodDays}d · all`,
      value: stats?.spike_entities_period ?? 0,
      subValue: [
        `Events: ${(stats?.total_spikes_period ?? stats?.total_spikes_today ?? 0).toLocaleString()}`,
        hasSpikeListTotal ? `Shown: ${Number(spikeListTotal).toLocaleString()}` : null,
      ].filter(Boolean).join(' · '),
      icon: Activity,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      isActive: activeMetric === 'spikes',
      onClick: () => handleMetricClick('spikes'),
    },
    {
      key: 'geos',
      label: 'GEOs with Spikes',
      meta: periodLabel,
      value: stats?.active_geos || 0,
      icon: Globe,
      color: 'text-chart-2',
      bgColor: 'bg-chart-2/10',
      isActive: activeMetric === 'geos',
      onClick: () => handleMetricClick('geos'),
    },
    {
      key: 'topgeo',
      label: 'Top Growing GEO',
      meta: periodLabel,
      value: topGeo,
      icon: TrendingUp,
      color: 'text-chart-4',
      bgColor: 'bg-chart-4/10',
      isText: true,
      isActive: activeMetric === 'topgeo' || selectedGeo === topGeo,
      onClick: () => handleMetricClick('topgeo'),
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {metrics.map((metric) => (
          <Card
            key={metric.key}
            className={`metric-card cursor-pointer transition-all ${
              metric.isActive
                ? 'ring-2 ring-primary border-transparent'
                : 'hover:border-primary/30'
            }`}
            onClick={metric.onClick}
          >
            <CardContent className="p-4 min-h-[126px]">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`text-[12px] mb-1.5 flex items-center gap-1.5 font-medium ${metric.isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    <span className="leading-tight">{metric.label}</span>
                    {metric.key === 'newbrands' && (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-300 text-[11px] font-semibold uppercase tracking-wide">
                        New
                      </span>
                    )}
                  </p>
                  {metric.meta && (
                    <p className="text-[11px] text-muted-foreground font-medium -mt-1 mb-1.5">
                      {metric.meta}
                    </p>
                  )}
                  <p className="text-3xl font-display font-bold text-foreground leading-none">
                    {metric.isText ? metric.value : metric.value.toLocaleString()}
                  </p>
                  {metric.subValue && (
                    <p className="text-[11px] text-muted-foreground font-medium mt-2 leading-tight">
                      {metric.subValue}
                    </p>
                  )}
                </div>
                <div className={`w-10 h-10 rounded-lg ${metric.isActive ? metric.bgColor : 'bg-muted/30'} flex items-center justify-center`}>
                  <metric.icon className={`w-5 h-5 ${metric.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
